import { TextAsset } from 'cc';

/**
 * 通用 fnt 解析器
 * @description 原理：
 * 1. 逐行讀取文件
 * 2. 每行按空格分割，陣列的第一個是主鍵，剩餘部分則視為子串
 * 3. 將子串逐個按 = 分割，左邊為子鍵，右邊為子值，根據子鍵可以按照自己的需求解析出子值（這裡是通用解析器，所以保留原始類型）
 * 4. 每次讀取一行進行解析，並記錄上一個主鍵和當前主鍵
 * 5. 解析步驟1：如果主鍵為空，則跳過（通常是空行）；如果主鍵不為空，則展開解析子鍵與子值
 * 6. 解析步驟2：如果當前主鍵與上一個主鍵相同，說明需要進行合併（將此主鍵內容視為陣列）
 * 7. 解析完成，將結果凍結為字串形式和 JSON 對象形式
 */
export default class FontParser {
    /** fnt的文字檔案 */
    private _textAsset: TextAsset;
    /** 輸出數據 */
    private _table: Record<string, Record<string, any>>;
    /** 輸出數據（字串形式） */
    private _resultString: string | null;
    /** 輸出數據（JSON 對象形式） */
    private _resultJSON: Record<string, Record<string, any>> | null;

    /**
     * 通用 fnt 解析器
     * @param path 輸入文件路徑
     */
    private constructor(path: TextAsset) {
        this._textAsset = path;
        this._table = {};
        this._resultString = null;
        this._resultJSON = null;
    }

    /**
     * 解析 Fnt 文件
     * @param textAsset 輸入文件
     * @returns BitmapFont 中的 fntConfig
     */
    public static parseFontConfig(textAsset: TextAsset) {
        const json = new FontParser(textAsset).parse().toJSON();
        const fntConfig: Record<string, any> = {
            commonHeight: json.common.lineHeight,
            fontSize: json.info.size,
            atlasName: json.page.file,
            fontDefDictionary: {},
            kerningDict: {},
        };
        const handleCharLine = (char: any) => {
            fntConfig.fontDefDictionary[char.id] = {
                rect: {
                    x: char.x,
                    y: char.y,
                    width: char.width,
                    height: char.height,
                },
                xOffset: char.xoffset,
                yOffset: char.yoffset,
                xAdvance: char.xadvance,
            };
        };
        if (Array.isArray(json.char)) {
            json.char.forEach((char: any) => {
                handleCharLine(char);
            });
        } else if (json.char) {
            handleCharLine(json.char);
        }

        return fntConfig;
    }

    /** 獲取字串形式的結果 */
    public toString() {
        return this._resultString!;
    }

    /** 獲取 JSON 對象形式的結果 */
    public toJSON() {
        return this._resultJSON!;
    }

    /** 解析 */
    public parse() {
        const lines = this._textAsset.text.split('\n');
        let line: string;
        let linePieces: string[];
        let preMainKey: string = '';
        let mainKey: string;
        let lineRest: string[];
        for (let i = 0, l = lines.length; i < l; i++) {
            line = lines[i];
            linePieces = line.split(' ');
            [mainKey, ...lineRest] = linePieces;
            this._parseLine(preMainKey, mainKey, lineRest);
            preMainKey = mainKey;
        }
        this._resultJSON = Object.freeze(this._table);
        this._resultString = JSON.stringify(this._resultJSON, null, 2);
        return this;
    }

    /**
     * 解析當前行
     * @param preMainKey 前一個主鍵
     * @param mainKey 當前主鍵
     * @param lineRest 剩餘部分
     * @returns
     */
    private _parseLine(preMainKey: string, mainKey: string, lineRest: string[]) {
        if (mainKey.length === 0) {
            return;
        }
        let subKey: string;
        let subVal: string;
        const table: Record<string, any> = {};
        for (let i = 0, l = lineRest.length; i < l; i++) {
            [subKey, subVal] = lineRest[i].split('=');
            table[subKey] = this._parseValue(subKey, subVal);
        }
        if (preMainKey === mainKey) {
            if (!Array.isArray(this._table[mainKey])) {
                this._table[mainKey] = [this._table[mainKey], table];
            } else {
                this._table[mainKey].push(table);
            }
        } else {
            this._table[mainKey] = table;
        }
    }

    /**
     * 解析子鍵與子值
     * @param key 子鍵
     * @param value 子值
     * @returns
     */
    private _parseValue(key: string, value: string) {
        let ret: any = value;
        // eslint-disable-next-line default-case
        switch (key) {
            case 'face':
            case 'charset':
            case 'file':
                ret = value.replace(/"/g, '');
                break;
            case 'size':
            case 'bold':
            case 'italic':
            case 'unicode':
            case 'stretchH':
            case 'smooth':
            case 'aa':
            case 'lineHeight':
            case 'base':
            case 'scaleW':
            case 'scaleH':
            case 'pages':
            case 'packed':
            case 'id':
            case 'count':
            case 'x':
            case 'y':
            case 'width':
            case 'height':
            case 'xoffset':
            case 'yoffset':
            case 'xadvance':
            case 'page':
            case 'chnl':
                ret = +value;
                break;
            case 'padding':
            case 'spacing':
                ret = value.split(',').map((x) => +x);
                break;
        }
        return ret;
    }
}
